import React, { useState, useRef, useEffect } from 'react';
import './AutomationPage.css';
import { Calendar, Clock, Users, Instagram, Zap, X, Plus, CheckCircle2, AlertCircle, Play, Pause, MoreVertical, Pencil, Trash2, TrendingUp, TrendingDown, Palette } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Progress } from '@/components/ui/Progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AutomationDetail from './AutomationDetail';
import { sendRequest } from '../api';

// Reuse the AutomationCard from AutomationLibrary
const AutomationCard = ({ automation, onEdit, className }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await sendRequest(`/automations/${automation.id}/metrics`, "GET");
        setMetrics(response.metrics);
      } catch (error) {
        console.error("Failed to fetch metrics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [automation.id]);

  const getGoalIcon = (goal) => {
    switch (goal) {
      case 'reach': return <Zap className="h-4 w-4" />;
      case 'engagement': return <Users className="h-4 w-4" />;
      case 'consistency': return <Calendar className="h-4 w-4" />;
      default: return <Zap className="h-4 w-4" />;
    }
  };

  const formatMetric = (value) => {
    if (typeof value !== 'number') return '0';
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  const getTrendColor = (trend) => {
    if (!trend) return 'text-gray-500';
    return trend > 0 ? 'text-green-500' : 'text-red-500';
  };

  const TrendArrow = ({ trend }) => {
    if (!trend) return null;
    const Icon = trend > 0 ? TrendingUp : TrendingDown;
    return <Icon className={`h-4 w-4 ${getTrendColor(trend)}`} />;
  };

  const platformIcons = {
    instagram: <Instagram className="h-3 w-3 sm:h-4 sm:w-4" />,
    tiktok: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3 sm:h-4 sm:w-4"
      >
        <path
          d="M10.8477 3.90796C10.8477 3.90796 10.8477 3.90796 10.8477 3.90796Z"
          fill="currentColor"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.7203 1.25H9.28025V9.5C9.28025 10.4665 8.49675 11.25 7.53025 11.25C6.56375 11.25 5.78025 10.4665 5.78025 9.5C5.78025 8.5335 6.56375 7.75 7.53025 7.75C7.58225 7.75 7.63325 7.7535 7.68425 7.7585V5.2915C7.63325 5.289 7.58225 5.2865 7.53025 5.2865C5.20525 5.2865 3.31525 7.1765 3.31525 9.5015C3.31525 11.8265 5.20525 13.7165 7.53025 13.7165C9.85525 13.7165 11.7453 11.8265 11.7453 9.5015V5.4465C12.5853 6.0415 13.5903 6.4115 14.6653 6.4115V3.948C13.0353 3.948 11.7203 2.7665 11.7203 1.25Z"
          fill="currentColor"
        />
      </svg>
    )
  };

  return (
    <Card className={cn(
      "p-4 sm:p-6 bg-white hover:shadow-md transition-all duration-200 rounded-2xl",
      className
    )}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-2 rounded-xl bg-blue-50">
            {getGoalIcon(automation.goal)}
          </div>
          <div>
            <h3 className="font-medium text-gray-900 text-sm sm:text-base">{automation.name}</h3>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {automation.platforms.map(platform => (
                <Badge 
                  key={platform}
                  className="
                    backdrop-blur-md bg-black/20 border border-white/10
                    text-white text-[10px] sm:text-xs
                    px-2 py-1 rounded-full
                    flex items-center gap-1.5
                    shadow shadow-black/5
                  "
                >
                  {platformIcons[platform]}
                  <span className="capitalize">{platform}</span>
                </Badge>
              ))}
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">{automation.description}</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuItem onClick={() => onEdit(automation)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem className="text-red-600">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-3 sm:mt-4 flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
          <span>{automation.schedule}</span>
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
          <span>{automation.nextPost}</span>
        </div>
      </div>

      <div className="mt-3 sm:mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          <span className="text-xs sm:text-sm font-medium text-green-700">
            {automation.status === 'paused' ? 'Paused' : 'Active'}
          </span>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="h-8 text-xs sm:text-sm gap-1.5"
        >
          <span className="sr-only">
            {automation.status === 'paused' ? 'Resume automation' : 'Pause automation'}
          </span>
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3 sm:h-4 sm:w-4"
          >
            <path
              d={automation.status === 'paused' 
                ? "M4.18179 7.17081L11.1818 12.1708L11.1818 2.17081L4.18179 7.17081Z" 
                : "M6.05 2.75C6.05 2.47386 5.82614 2.25 5.55 2.25C5.27386 2.25 5.05 2.47386 5.05 2.75V12.25C5.05 12.5261 5.27386 12.75 5.55 12.75C5.82614 12.75 6.05 12.5261 6.05 12.25V2.75ZM9.95 2.75C9.95 2.47386 9.72614 2.25 9.45 2.25C9.17386 2.25 8.95 2.47386 8.95 2.75V12.25C8.95 12.5261 9.17386 12.75 9.45 12.75C9.72614 12.75 9.95 12.5261 9.95 12.25V2.75Z"}
              fill="currentColor"
              fillRule="evenodd"
              clipRule="evenodd"
            ></path>
          </svg>
          <span className="hidden sm:inline">
            {automation.status === 'paused' ? 'Resume' : 'Pause'}
          </span>
        </Button>
      </div>

      <div className="mt-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-500 mb-1">Videos</div>
            <div className="flex items-center justify-between">
              <div className="font-medium">
                {formatMetric(automation.videos_count || 0)}
              </div>
              {metrics?.videos_trend && (
                <div className="flex items-center gap-1 text-sm">
                  <span className={getTrendColor(metrics.videos_trend)}>
                    {metrics.videos_trend > 0 ? '+' : ''}{metrics.videos_trend}%
                  </span>
                  <TrendArrow trend={metrics.videos_trend} />
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-500 mb-1">Engagement</div>
            <div className="flex items-center justify-between">
              <div className="font-medium">
                {metrics?.engagement || '0%'}
              </div>
              {metrics?.engagement_trend && (
                <div className="flex items-center gap-1 text-sm">
                  <span className={getTrendColor(metrics.engagement_trend)}>
                    {metrics.engagement_trend > 0 ? '+' : ''}{metrics.engagement_trend}%
                  </span>
                  <TrendArrow trend={metrics.engagement_trend} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-500">Schedule Progress</div>
            <div className="text-sm font-medium">
              {metrics?.completed_posts || 0}/{metrics?.total_posts || 0}
            </div>
          </div>
          <Progress 
            value={metrics ? (metrics.completed_posts / metrics.total_posts) * 100 : 0} 
            className="h-2"
          />
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="text-gray-500">Next Post</div>
          <div className="font-medium">
            {automation.next_post_time ? (
              format(new Date(automation.next_post_time), 'MMM d, h:mm a')
            ) : (
              'Not scheduled'
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const BRAND_COLORS = [
  { name: 'Periwinkle', value: '#818cf8' },   // Soft indigo
  { name: 'Lavender', value: '#a78bfa' },     // Soft purple
  { name: 'Rose', value: '#f472b6' },         // Soft pink
  { name: 'Sky', value: '#60a5fa' },          // Soft blue
  { name: 'Aqua', value: '#67e8f9' },         // Soft cyan
  { name: 'Mint', value: '#5eead4' },         // Soft teal
  { name: 'Sage', value: '#86efac' },         // Soft green
  { name: 'Peach', value: '#fdba74' },        // Soft orange
  { name: 'Coral', value: '#fca5a5' },        // Soft red
  { name: 'Lilac', value: '#c4b5fd' },        // Soft violet
];

const CreateAutomationModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    timing: 'ai',
    goal: 'reach',
    platforms: ['instagram'],
    color: '#6366f1', // Default indigo color
    schedule: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    }
  });

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        timing: 'ai',
        goal: 'reach',
        platforms: ['instagram'],
        color: '#6366f1',
        schedule: {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        }
      });
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      alert('Please enter a name for your automation');
      return;
    }
    
    // Validate schedule if custom timing is selected
    if (formData.timing === 'custom') {
      const hasTimeSlots = Object.values(formData.schedule).some(slots => slots.length > 0);
      if (!hasTimeSlots) {
        alert('Please set at least one time slot for custom scheduling');
        return;
      }
    }
    
    onSubmit(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] w-[calc(100%-32px)] p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">Create New Automation</DialogTitle>
        </DialogHeader>
        <div className="py-4 overflow-auto max-h-[calc(100vh-200px)] sm:max-h-[600px]">
          <div className="space-y-6 sm:space-y-8">
            {/* Name Input */}
            <div className="space-y-3">
              <label 
                htmlFor="automation-name" 
                className="text-sm sm:text-base font-medium text-gray-700"
              >
                Automation Name
              </label>
              <input
                id="automation-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Peak Hours Posting"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         text-sm sm:text-base"
              />
            </div>

            <SchedulingRules 
              selectedTiming={formData.timing}
              onSelect={(timing) => setFormData({ ...formData, timing })}
              schedule={formData.schedule}
              onScheduleChange={(schedule) => setFormData({ ...formData, schedule })}
            />

            {/* Color Picker */}
            <div className="space-y-3">
              <label 
                className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700"
              >
                <Palette className="h-4 w-4" />
                Calendar Color
              </label>
              <div className="space-y-3">
                <div className="grid grid-cols-5 gap-3">
                  {BRAND_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={cn(
                        "w-10 h-10 rounded-full transition-all duration-200",
                        "hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2",
                        "flex items-center justify-center",
                        formData.color === color.value ? "ring-2 ring-offset-2" : ""
                      )}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    >
                      {formData.color === color.value && (
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      )}
                    </button>
                  ))}
                </div>
                <span className="block text-sm text-gray-500">
                  Choose a color to identify this automation's posts in the calendar view
                </span>
              </div>
            </div>

            {/* Show Goal selection only if AI timing is selected */}
            {formData.timing === 'ai' && (
              <GoalDropdown 
                selectedGoal={formData.goal}
                onSelect={(goal) => setFormData({ ...formData, goal })}
              />
            )}

            <PlatformSelection 
              selectedPlatforms={formData.platforms}
              onSelect={(platforms) => setFormData({ ...formData, platforms })}
            />
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={onClose} 
                className="text-sm rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-sm rounded-xl"
                onClick={handleSubmit}
                disabled={!formData.name.trim()}
              >
                Create Automation
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SchedulingRules = ({ selectedTiming, onSelect, schedule, onScheduleChange }) => (
  <div className="scheduling-rules">
    <div className="section-header">
      <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
      <h3 className="text-sm sm:text-base">When to Post</h3>
    </div>
    <div className="rule-options">
      <label className="rule-option">
        <input 
          type="radio" 
          name="timing" 
          checked={selectedTiming === 'ai'}
          onChange={() => onSelect('ai')}
        />
        <div className="rule-content">
          <span className="rule-title text-sm sm:text-base">AI-Powered Timing</span>
          <span className="rule-description text-xs sm:text-sm">Let AI determine the best times based on your audience's activity</span>
        </div>
      </label>
      <label className="rule-option">
        <input 
          type="radio" 
          name="timing"
          checked={selectedTiming === 'custom'}
          onChange={() => onSelect('custom')}
        />
        <div className="rule-content">
          <span className="rule-title text-sm sm:text-base">Custom Schedule</span>
          <span className="rule-description text-xs sm:text-sm">Set your own posting schedule</span>
        </div>
      </label>
    </div>
    {selectedTiming === 'custom' && (
      <CustomScheduleSettings 
        schedule={schedule}
        onScheduleChange={onScheduleChange}
      />
    )}
  </div>
);

const GoalDropdown = ({ selectedGoal, onSelect }) => (
  <div className="goal-selector">
    <div className="flex items-center gap-2 mb-3 sm:mb-4">
      <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
      <h2 className="text-sm sm:text-base font-medium text-gray-700">What's your goal?</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
      <button 
        className={cn("goal-option", selectedGoal === 'reach' && "active")}
        onClick={() => onSelect('reach')}
      >
        <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
        <span className="text-sm">Maximize Reach</span>
      </button>
      <button 
        className={cn("goal-option", selectedGoal === 'engagement' && "active")}
        onClick={() => onSelect('engagement')}
      >
        <Users className="h-4 w-4 sm:h-5 sm:w-5" />
        <span className="text-sm">Engage Audience</span>
      </button>
      <button 
        className={cn("goal-option", selectedGoal === 'consistency' && "active")}
        onClick={() => onSelect('consistency')}
      >
        <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
        <span className="text-sm">Consistent Posting</span>
      </button>
    </div>
  </div>
);

const CustomScheduleSettings = ({ schedule, onScheduleChange }) => {
  const addTimeSlot = (day) => {
    onScheduleChange({
      ...schedule,
      [day]: [...schedule[day], { time: "12:00", period: "PM" }]
    });
  };

  const removeTimeSlot = (day, index) => {
    onScheduleChange({
      ...schedule,
      [day]: schedule[day].filter((_, i) => i !== index)
    });
  };

  const updateTimeSlot = (day, index, time, period) => {
    onScheduleChange({
      ...schedule,
      [day]: schedule[day].map((slot, i) => 
        i === index ? { time, period } : slot
      )
    });
  };

  const days = [
    'monday', 'tuesday', 'wednesday', 'thursday',
    'friday', 'saturday', 'sunday'
  ];

  const timeOptions = Array.from({ length: 12 }, (_, i) => {
    const hour = (i + 1).toString().padStart(2, '0');
    return [
      `${hour}:00`,
      `${hour}:30`
    ];
  }).flat();

  return (
    <div className="mt-6 space-y-4 border rounded-lg p-4 bg-white/50 backdrop-blur-sm">
      <div className="text-sm font-medium text-gray-700 mb-4">
        Choose your posting times for each day
      </div>
      
      <div className="space-y-4">
        {days.map(day => (
          <div key={day} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium capitalize">{day}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addTimeSlot(day)}
                className="h-7 text-xs rounded-xl"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Time
              </Button>
            </div>
            
            {schedule[day].length === 0 ? (
              <div className="text-xs text-gray-500 italic">
                No times set for {day}
              </div>
            ) : (
              <div className="space-y-2">
                {schedule[day].map((slot, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <select
                      value={slot.time}
                      onChange={(e) => updateTimeSlot(day, index, e.target.value, slot.period)}
                      className="text-sm p-1 border rounded"
                    >
                      {timeOptions.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    <select
                      value={slot.period}
                      onChange={(e) => updateTimeSlot(day, index, slot.time, e.target.value)}
                      className="text-sm p-1 border rounded"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTimeSlot(day, index)}
                      className="h-7 w-7 p-0 rounded-xl"
                    >
                      <X className="h-3 w-3 text-gray-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const PlatformSelection = ({ selectedPlatforms, onSelect }) => (
  <div className="platform-selection">
    <div className="space-y-2">
      <div className="section-header">
        <Instagram className="h-4 w-4 sm:h-5 sm:w-5" />
        <h3 className="text-sm sm:text-base">Platforms</h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">
        Select the social media platforms where you'd like your content to be automatically shared. Your posts will be optimized for each platform's specific format and requirements.
      </p>
    </div>
    <div className="platform-options mt-4">
      <label className="platform-option">
        <input 
          type="checkbox" 
          checked={selectedPlatforms.includes('instagram')}
          onChange={(e) => {
            if (e.target.checked) {
              onSelect([...selectedPlatforms, 'instagram']);
            } else {
              onSelect(selectedPlatforms.filter(p => p !== 'instagram'));
            }
          }}
        />
        <div className="platform-content">
          <span className="platform-name text-sm sm:text-base">Instagram</span>
          <span className="platform-stats text-xs sm:text-sm">12M+ followers</span>
        </div>
      </label>
      <label className="platform-option">
        <input 
          type="checkbox"
          checked={selectedPlatforms.includes('tiktok')}
          onChange={(e) => {
            if (e.target.checked) {
              onSelect([...selectedPlatforms, 'tiktok']);
            } else {
              onSelect(selectedPlatforms.filter(p => p !== 'tiktok'));
            }
          }}
        />
        <div className="platform-content">
          <span className="platform-name text-sm sm:text-base">TikTok</span>
          <span className="platform-stats text-xs sm:text-sm">5M+ followers</span>
        </div>
      </label>
    </div>
  </div>
);

// iOS-specific breakpoints and constants
const IOS_BREAKPOINTS = {
  iPhone: '390px',    // iPhone 12/13/14
  iPhonePlus: '428px' // iPhone Pro Max
};

const IOS_SAFE_AREAS = {
  top: 'env(safe-area-inset-top, 47px)',      // Default notch height
  bottom: 'env(safe-area-inset-bottom, 34px)', // Default home indicator height
};

// Enhanced animations and transitions
const SPRING_CONFIG = {
  type: "spring",
  stiffness: 300,
  damping: 30
};

const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: SPRING_CONFIG
  }
};

// Main Oplaes Component
const Oplaes = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const pullToRefreshRef = useRef(null);
  const startY = useRef(0);
  const navigate = useNavigate();
  const [automations, setAutomations] = useState([
    {
      id: 1,
      name: "Peak Hours Posting",
      description: "Auto-post during highest engagement times",
      goal: "reach",
      schedule: "3x per day",
      nextPost: "Today at 6:00 PM",
      status: "active",
      platforms: ["instagram", "tiktok"],
      metrics: {
        posts: 45,
        views: "125K",
        engagement: "8.5%",
        trend: "+12%"
      }
    },
    {
      id: 2,
      name: "Weekend Content",
      description: "Schedule weekend content for maximum reach",
      goal: "engagement",
      schedule: "Weekends only",
      nextPost: "Saturday at 2:00 PM",
      status: "active",
      platforms: ["instagram"],
      metrics: {
        posts: 28,
        views: "82K",
        engagement: "6.2%",
        trend: "+8%"
      }
    }
  ]);

  const filteredAutomations = automations.filter(automation => {
    if (selectedPlatform === 'all') return true;
    return automation.platforms.includes(selectedPlatform);
  });

  const handleEdit = (automation) => {
    console.log('Edit automation:', automation);
    setShowCreateModal(true);
  };

  const handleDuplicate = (automation) => {
    const newAutomation = {
      ...automation,
      id: Date.now(),
      name: `${automation.name} (Copy)`,
    };
    setAutomations([...automations, newAutomation]);
  };

  const handleDelete = (automation) => {
    if (window.confirm('Are you sure you want to delete this automation?')) {
      setAutomations(automations.filter(a => a.id !== automation.id));
    }
  };

  const handleTogglePause = (automation) => {
    setAutomations(automations.map(a => {
      if (a.id === automation.id) {
        return {
          ...a,
          status: a.status === 'paused' ? 'active' : 'paused'
        };
      }
      return a;
    }));
  };

  const handleCreateAutomation = (formData) => {
    const newAutomation = {
      id: Date.now(),
      name: formData.name,
      description: `${formData.goal === 'reach' ? 'Maximize reach' : formData.goal === 'engagement' ? 'Engage audience' : 'Consistent posting'} using ${formData.timing === 'ai' ? 'AI-powered timing' : 'custom schedule'}`,
      goal: formData.goal,
      schedule: formData.timing === 'ai' ? 'AI-Powered' : 'Custom',
      nextPost: 'Calculating...',
      status: 'active',
      platforms: formData.platforms
    };
    
    setAutomations([...automations, newAutomation]);
    setShowCreateModal(false);
  };

  const handleAutomationClick = (automation) => {
    setSelectedAutomation(automation);
  };

  // Pull-to-refresh logic
  useEffect(() => {
    const element = pullToRefreshRef.current;
    
    const handleTouchStart = (e) => {
      startY.current = e.touches[0].pageY;
    };

    const handleTouchMove = (e) => {
      const currentY = e.touches[0].pageY;
      const diff = currentY - startY.current;
      
      if (diff > 50 && window.scrollY === 0 && !refreshing) {
        setRefreshing(true);
        // Simulate refresh
        setTimeout(() => {
          setRefreshing(false);
        }, 1500);
      }
    };

    if (element) {
      element.addEventListener('touchstart', handleTouchStart);
      element.addEventListener('touchmove', handleTouchMove);
    }

    return () => {
      if (element) {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [refreshing]);

  return (
    <div 
      ref={pullToRefreshRef}
      className={cn(
        "min-h-screen bg-gradient-to-b from-gray-50 to-gray-100",
        // iOS-specific padding and safe areas
        "p-4 pt-[calc(env(safe-area-inset-top,_47px)_+_1rem)]",
        "pb-[calc(env(safe-area-inset-bottom,_34px)_+_1rem)]",
        // Responsive padding
        "sm:p-6 md:p-8 md:pt-16",
        "overflow-x-hidden" // Prevent horizontal scroll during animations
      )}
    >
      <AnimatePresence mode="wait">
        {selectedAutomation ? (
          <AutomationDetail
            automation={selectedAutomation}
            onClose={() => setSelectedAutomation(null)}
          />
        ) : (
          <>
            {/* Pull to refresh indicator */}
            <AnimatePresence>
              {refreshing && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="fixed top-0 left-0 right-0 flex justify-center items-center py-2 bg-white/80 backdrop-blur-sm"
                >
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Platform Filter - Mobile Optimized */}
            <motion.div 
              className="fixed top-[var(--navbar-height)] left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-200"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={SPRING_CONFIG}
            >
              <div className="max-w-md mx-auto px-4 sm:px-6 md:px-8 py-2">
                <div className="flex justify-center space-x-1.5">
                  <motion.button
                      onClick={() => setSelectedPlatform('all')}
                    className={cn(
                      "flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                      selectedPlatform === 'all'
                        ? "bg-indigo-600 text-white shadow-md"
                        : "bg-gray-100/80 text-gray-600 hover:bg-gray-200"
                    )}
                    whileTap={{ scale: 0.95 }}
                    >
                      All Platforms
                  </motion.button>
                  <motion.button
                      onClick={() => setSelectedPlatform('instagram')}
                    className={cn(
                      "flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                      selectedPlatform === 'instagram'
                        ? "bg-indigo-600 text-white shadow-md"
                        : "bg-gray-100/80 text-gray-600 hover:bg-gray-200",
                      "flex items-center justify-center gap-2"
                    )}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Instagram className="w-4 h-4" />
                      Instagram
                  </motion.button>
                  <motion.button
                      onClick={() => setSelectedPlatform('tiktok')}
                    className={cn(
                      "flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                      selectedPlatform === 'tiktok'
                        ? "bg-indigo-600 text-white shadow-md"
                        : "bg-gray-100/80 text-gray-600 hover:bg-gray-200",
                      "flex items-center justify-center gap-2"
                    )}
                    whileTap={{ scale: 0.95 }}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11.7203 1.25H9.28025V9.5C9.28025 10.4665 8.49675 11.25 7.53025 11.25C6.56375 11.25 5.78025 10.4665 5.78025 9.5C5.78025 8.5335 6.56375 7.75 7.53025 7.75C7.58225 7.75 7.63325 7.7535 7.68425 7.7585V5.2915C7.63325 5.289 7.58225 5.2865 7.53025 5.2865C5.20525 5.2865 3.31525 7.1765 3.31525 9.5015C3.31525 11.8265 5.20525 13.7165 7.53025 13.7165C9.85525 13.7165 11.7453 11.8265 11.7453 9.5015V5.4465C12.5853 6.0415 13.5903 6.4115 14.6653 6.4115V3.948C13.0353 3.948 11.7203 2.7665 11.7203 1.25Z"
                          fill="currentColor"
                        />
                      </svg>
                      TikTok
                  </motion.button>
                  </div>
                </div>
            </motion.div>

            {/* Main Content */}
            <motion.div
              key={selectedPlatform}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={SPRING_CONFIG}
              className="pt-[76px]" // Add padding to account for fixed filter
            >
              {/* Hero Section - Enhanced with Motion */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={CARD_VARIANTS}
                className="mb-4 sm:mb-8"
              >
                <Card className="bg-white rounded-2xl shadow-lg border border-indigo-50 overflow-hidden">
                  <CardContent className="p-4 sm:p-8">
                    <motion.h1 
                      className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-transparent bg-clip-text"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      Your Automations
                    </motion.h1>
                    <motion.p 
                      className="text-base sm:text-lg text-gray-600 mt-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      Manage and monitor your content automation rules
                    </motion.p>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Automation Cards Grid - Mobile Optimized */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-24 sm:mb-0">
                {filteredAutomations.map((automation, index) => (
                  <motion.div
                      key={automation.id}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { 
                        opacity: 1, 
                        y: 0,
                        transition: { 
                          delay: index * 0.1,
                          ...SPRING_CONFIG
                        }
                      }
                    }}
                    initial="hidden"
                    animate="visible"
                    onClick={() => handleAutomationClick(automation)}
                    className="cursor-pointer"
                  >
                    <AutomationCard 
                      automation={automation}
                      onEdit={handleEdit}
                      className="h-full"
                    />
                  </motion.div>
              ))}
            </div>

              {/* Create New Automation Button - Fixed to Bottom on Mobile but above footer */}
              <motion.div 
                className="fixed bottom-[calc(env(safe-area-inset-bottom,_34px)_+_4rem)] left-0 right-0 p-4 
                          bg-gradient-to-t from-gray-50 to-transparent 
                          sm:relative sm:p-0 sm:mt-12 sm:bottom-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={SPRING_CONFIG}
              >
              <Button
                onClick={() => setShowCreateModal(true)}
                  className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 
                         text-white shadow-lg shadow-purple-500/25 rounded-xl py-6 sm:py-2"
              >
                <Zap className="h-4 w-4 mr-2" />
                Create New Automation
              </Button>
              </motion.div>

              <CreateAutomationModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSubmit={handleCreateAutomation}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Oplaes; 